import axios from "axios";
import {
  getAuthorization,
  getRefreshToken,
  setAuthorization,
} from "config/service/auth-service";

const BACKOFFICE_PREFIX = "/api/breakage/v1/backoffice";

let isRefreshing = false;
let failedRequestQueue = [];

const toBearerToken = (token) => {
  if (!token) {
    return null;
  }
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
};

const buildParams = (params = {}) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          search.append(key, item);
        }
      });
      return;
    }

    search.append(key, value);
  });

  return search;
};

const breakageHttp = axios.create({
  baseURL: process.env.REACT_APP_BREAKAGE_API_URL,
  responseType: "json",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  paramsSerializer: {
    serialize: (params) => buildParams(params).toString(),
  },
});

const setupBreakageInterceptors = () => {
  breakageHttp.defaults.baseURL = process.env.REACT_APP_BREAKAGE_API_URL;

  breakageHttp.interceptors.request.use(
    (config) => {
      const token = toBearerToken(getAuthorization());
      if (token) {
        config.headers.Authorization = token;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Breakage 401/403 are product-auth outcomes (missing trusted identity,
  // permission, or Owner scope). Never clear the legacy BackOffice session
  // here — that belongs only to REACT_APP_API_URL interceptors.
  breakageHttp.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!error.response || error.response.status !== 401 || !originalRequest) {
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestQueue.push({
            onSuccess: (token) => {
              originalRequest.headers.Authorization = toBearerToken(token);
              resolve(breakageHttp(originalRequest));
            },
            onFailure: (err) => reject(err),
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const client = axios.create();
        const { data } = await client.post(
          `${process.env.REACT_APP_API_URL}/auth/refresh`,
          {
            accessToken: getAuthorization(),
            refreshToken,
          }
        );

        const newAccessToken = data.accessToken;
        const newRefreshToken = data.refreshToken;
        setAuthorization(newAccessToken, newRefreshToken);

        failedRequestQueue.forEach((request) =>
          request.onSuccess(newAccessToken)
        );
        failedRequestQueue = [];

        originalRequest.headers.Authorization = toBearerToken(newAccessToken);
        return breakageHttp(originalRequest);
      } catch (refreshError) {
        failedRequestQueue.forEach((request) =>
          request.onFailure(refreshError)
        );
        failedRequestQueue = [];
        // Refresh failed: leave session to the legacy API interceptor.
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

export { breakageHttp, setupBreakageInterceptors, BACKOFFICE_PREFIX };
